import React from 'react';

import classNames from 'classnames';
import styled, { css } from 'styled-components';

import ChevronRight from 'common/Icons/ChevronRight';
import { ActionPlanCompanyStatsType } from 'types/actionPlans';
import { pluralizeWord } from 'utils/helpers/string';

import { ACTION_PLANS_COMPANY_SUMMARY_TITLE } from './constants';

const Container = styled.div`
  display: flex;
  gap: 20px;
`;

const cardStyles = css`
  flex: 1 1 0;
  box-sizing: border-box;
  min-width: 0;
  min-height: 66px;
  padding: 10px 15px;
  border: 1px solid var(--line-gray);
  border-radius: 8px;
`;

const StaticCard = styled.div`
  ${cardStyles};
  display: flex;
  align-items: center;
  gap: 12px;
`;

const OverdueCard = styled.button`
  ${cardStyles};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: inherit;
  font: inherit;
  text-align: left;
  background: var(--white);
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background 0.15s ease;

  &:hover {
    border-color: var(--line-gray-strong);
  }

  &.selected {
    border-color: var(--button-blue);
    box-shadow: inset 0 0 0 1px var(--button-blue);
    background: var(--light-blue);
  }
`;

const OverdueContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  overflow: hidden;
`;

const IconBox = styled.div`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  &.pink {
    background-image: linear-gradient(to bottom, var(--pink-pale), var(--pink-soft) 100%);
  }

  &.mint {
    background-image: linear-gradient(to bottom, var(--mint-pale), var(--mint-soft));
  }

  &.gold {
    background-image: linear-gradient(to bottom, var(--gold-pale), var(--gold-soft));
  }

  &.blue {
    background-image: linear-gradient(to bottom, var(--blue-mist), var(--background-light-blue));
  }
`;

const IconImage = styled.img`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
`;

const OverdueIcon = styled(IconImage)`
  width: 24px;
  height: 24px;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const NumericBody = styled(Body)`
  gap: 4px;
`;

const Title = styled.span`
  font-size: 14px;
  font-family: var(--font-regular);
  font-weight: 400;
  color: var(--text-primary);
  white-space: nowrap;
`;

const Subtitle = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 12px;
  color: var(--text-primary);
  white-space: nowrap;
`;

const Count = styled.span`
  font-size: 28px;
  line-height: 1;
  font-family: var(--font-bold);
  color: var(--text-primary);
`;

const Unit = styled.span`
  font-size: 14px;
  font-family: var(--font-regular);
  color: var(--text-primary);
`;

const FocusAreaSubtitle = styled.div`
  padding-top: 5px;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 16px;
  font-family: var(--font-bold);
`;

type Props = {
  stats: ActionPlanCompanyStatsType | undefined;
  isOverall: boolean;
  communityCount: number;
  focusAreaLabel?: string;
  overdueActive: boolean;
  onToggleOverdue: () => void;
};

const ActionPlansCompanySummary = ({
  stats,
  isOverall,
  communityCount,
  focusAreaLabel,
  overdueActive,
  onToggleOverdue,
}: Props) => {
  const mostCommonFocusAreaLabel = stats?.most_common_focus_area ?? '-';
  const communityLabel = pluralizeWord(communityCount, 'community', 'communities');
  const getTitle = (title: string) => (focusAreaLabel ? `${title} (${focusAreaLabel})` : title);

  return (
    <Container>
      <StaticCard data-test-id="action-plans-company-total-plans">
        <IconBox className="pink">
          <IconImage src="/icons/checklist-red.svg" alt="" />
        </IconBox>
        <NumericBody>
          <Title>{getTitle(ACTION_PLANS_COMPANY_SUMMARY_TITLE.TOTAL_PLANS)}</Title>
          <Subtitle>
            <Count>{stats?.total_plans ?? 0}</Count>
            <Unit>{`across ${communityCount} ${communityLabel}`}</Unit>
          </Subtitle>
        </NumericBody>
      </StaticCard>
      <StaticCard data-test-id="action-plans-company-completion-rate">
        <IconBox className="mint">
          <IconImage src="/icons/check-green.svg" alt="" />
        </IconBox>
        <NumericBody>
          <Title>{getTitle(ACTION_PLANS_COMPANY_SUMMARY_TITLE.COMPLETION_RATE)}</Title>
          <Subtitle>
            <Count>{stats?.completion_rate ?? 0}</Count>
            <Unit>%</Unit>
          </Subtitle>
        </NumericBody>
      </StaticCard>
      <OverdueCard
        type="button"
        className={classNames({ selected: overdueActive })}
        aria-pressed={overdueActive}
        data-test-id="action-plans-company-overdue"
        onClick={onToggleOverdue}
      >
        <OverdueContent>
          <IconBox className="gold">
            <OverdueIcon src="/icons/sentiment-dissatisfied.svg" alt="" />
          </IconBox>
          <NumericBody>
            <Title>{getTitle(ACTION_PLANS_COMPANY_SUMMARY_TITLE.OVERDUE)}</Title>
            <Count>{stats?.overdue_count ?? 0}</Count>
          </NumericBody>
        </OverdueContent>
        <ChevronRight fill="var(--text-secondary)" width={20} height={20} />
      </OverdueCard>
      {isOverall && (
        <StaticCard data-test-id="action-plans-company-most-common-focus-area">
          <IconBox className="blue">
            <IconImage src="/icons/local-offer-blue.svg" alt="" />
          </IconBox>
          <Body>
            <Title>{ACTION_PLANS_COMPANY_SUMMARY_TITLE.MOST_COMMON_FOCUS_AREA}</Title>
            <FocusAreaSubtitle title={mostCommonFocusAreaLabel}>{mostCommonFocusAreaLabel}</FocusAreaSubtitle>
          </Body>
        </StaticCard>
      )}
    </Container>
  );
};

export default ActionPlansCompanySummary;
